// ═══════════════════════════════════════════════════════════════════════════════
// Employer — Overdue tasks grouped by due date
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { EmployerTaskSubPageHeader } from '../../components/task/employer/EmployerTaskSubPageHeader';
import { EmployerOverdueStatusIcon } from '../../components/task/employer/EmployerOverdueStatusIcon';
import { EmployerStatusRibbon } from '../../components/task/employer/EmployerStatusRibbon';
import { ListNavRow } from '../../components/ui/ListNavRow';
import { SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW } from '../../lib/layoutChrome';
import { taskInstanceApi } from '../../lib/api';
import { navigateBack } from '../../lib/navigateBack';
import { buildEmployerGroupedTaskDateHeading } from '../../utils/employerTaskDateHeading';
import {
  addCalendarDaysToYmd,
  getDateInTimezone,
} from '../../utils/employerTaskDayGrouping';
import { resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import type { EmployerOverdueTaskItem } from '@fdh/shared';

const parseDueTimeToMinutes = (dueTime: string): number | null => {
  const trimmed = dueTime.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return hour * 60 + minute;
};

const compareEmployerOverdueByDueTime = (
  a: EmployerOverdueTaskItem,
  b: EmployerOverdueTaskItem,
): number => {
  const ma = a.dueTime ? parseDueTimeToMinutes(a.dueTime) : null;
  const mb = b.dueTime ? parseDueTimeToMinutes(b.dueTime) : null;
  if (ma === null && mb === null) return 0;
  if (ma === null) return 1;
  if (mb === null) return -1;
  return ma - mb;
};

export const EmployerOverdueTasksPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const timezone = resolveDisplayTimeZone(user?.settings.timezone);
  const todayYmd = useMemo(() => getDateInTimezone(timezone), [timezone]);
  const tomorrowYmd = useMemo(() => addCalendarDaysToYmd(todayYmd, 1), [todayYmd]);

  const [overdueTasks, setOverdueTasks] = useState<EmployerOverdueTaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOverdue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await taskInstanceApi.overdueList();
      if (!response.success) {
        setError(response.error ?? t('task.list.loadError'));
        return;
      }
      setOverdueTasks(response.data?.tasks ?? []);
    } catch {
      setError(t('task.list.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchOverdue();
  }, [fetchOverdue]);

  const overdueTasksGrouped = useMemo(() => {
    const byDate = new Map<string, EmployerOverdueTaskItem[]>();
    overdueTasks.forEach((task) => {
      const d = task.dueDate;
      const list = byDate.get(d) ?? [];
      list.push(task);
      byDate.set(d, list);
    });
    const dates = Array.from(byDate.keys()).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
    return dates.map((date) => ({
      date,
      tasks: (byDate.get(date) ?? [])
        .slice()
        .sort((a, b) => {
          const cmp = compareEmployerOverdueByDueTime(a, b);
          if (cmp !== 0) return cmp;
          return a.title.localeCompare(b.title);
        }),
    }));
  }, [overdueTasks]);

  const handleBack = () => {
    navigateBack(navigate, '/tasks');
  };

  const handleTaskInstanceDetailClick = (taskInstanceId: string) => {
    navigate(`/task-instances/${taskInstanceId}`);
  };

  return (
    <div className="min-h-full bg-[#f7f8fb] dark:bg-gray-950">
      <EmployerTaskSubPageHeader title={t('task.list.tabOverdueTask')} onBack={handleBack} />

      <main className={`mx-auto max-w-lg px-4 py-6 ${SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW}`}>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : error ? (
          <div className="rounded-xl bg-red-50 p-6 text-center dark:bg-red-950/40">
            <p className="text-red-600 dark:text-red-300" role="alert">
              {error}
            </p>
            <button
              type="button"
              onClick={() => void fetchOverdue()}
              className="mt-3 text-sm font-medium text-red-700 underline hover:no-underline focus:outline-none dark:text-red-200"
            >
              {t('task.action.refresh')}
            </button>
          </div>
        ) : overdueTasks.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm dark:bg-gray-900">
            <p className="text-gray-600 dark:text-gray-300">{t('task.list.overdueEmpty')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {overdueTasksGrouped.map((group) => (
              <section
                key={group.date}
                className="space-y-2"
                aria-labelledby={`employer-task-overdue-date-${group.date}`}
              >
                <h2
                  id={`employer-task-overdue-date-${group.date}`}
                  className="flex items-baseline justify-between gap-2 text-sm font-semibold tracking-tight text-gray-600 dark:text-gray-300"
                >
                  <span className="min-w-0">
                    {buildEmployerGroupedTaskDateHeading({
                      dateYmd: group.date,
                      todayYmd,
                      tomorrowYmd,
                      timezone,
                      language,
                      t,
                    })}
                  </span>
                  <span className="shrink-0 font-semibold text-emerald-600 dark:text-emerald-400">
                    {t('task.history.groupTotalCount', { count: group.tasks.length })}
                  </span>
                </h2>
                <ul className="space-y-3" role="list">
                  {group.tasks.map((task) => (
                    <li key={task.taskInstanceId}>
                      <ListNavRow
                        onClick={() => handleTaskInstanceDetailClick(task.taskInstanceId)}
                        ariaLabel={task.title}
                        tone="danger"
                        comfortable
                        ribbon={<EmployerStatusRibbon status={task.status} submittedAt="" />}
                        leading={<EmployerOverdueStatusIcon variant="list" />}
                      >
                        <div className="min-w-0 pr-10">
                          <h3 className="truncate font-semibold text-gray-900 dark:text-gray-100">{task.title}</h3>
                          {task.dueTime ? (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {t('task.today.dueAt', { time: task.dueTime })}
                            </p>
                          ) : null}
                        </div>
                      </ListNavRow>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
